import classNames from 'classnames';
import { useCallback } from 'react';
import { Badge, Button, Heading } from 'yoso';
import { useAdventure, useProceedJoinAdventure } from '~/components/Adventure/Adventure.hooks';
import { BaseCard } from '~/components/Card';
import { Countdown } from '~/components/Countdown/v2/Countdown';
import heroStyles from '~/components/Hero/Hero.module.scss';
import { T } from '~/components/I18n';
import { DescriptionList } from '~/components/List/Description/DescriptionList';
import { regularFormat } from '~/components/Util';
import type { AdventureHeroProps } from '../AdventureHero';
import styles from '../AdventureHero.module.scss';

export const InProgressAdventureHero = ({ id, className }: AdventureHeroProps) => {
  const { data: { startAt, endAt, announcedAt, participation } = {}, isInitialLoading } =
    useAdventure(id);
  const { maxCount = Infinity, currentCount = 0 } = participation || {};
  const { mutate, isLoading } = useProceedJoinAdventure();
  const handleButtonClick = useCallback(() => mutate(id), [id, mutate]);

  const buttonDisabled = isInitialLoading || maxCount <= currentCount;

  return (
    <BaseCard className={classNames(styles.root, heroStyles.root, className)} size="large">
      <Badge className={heroStyles.badge} color="secondary">
        <T name="citizen_event_status_live" />
      </Badge>
      <Heading level={3} className={heroStyles.heading}>
        <T name="citizen_adventure_title_live" options={[id]} />
      </Heading>
      <DescriptionList className={heroStyles.list}>
        <dt>
          <strong>
            <T name="citizen_event_remainingtime" />
          </strong>
        </dt>
        <dd>
          <Countdown className={heroStyles.heading} value={endAt} />
        </dd>
      </DescriptionList>
      <DescriptionList layout="responsive" className={heroStyles.sublist}>
        <dt>
          <T name="citizen_arcade_game_challenge_desc1" />
        </dt>
        <dd className={heroStyles.date}>
          {regularFormat(startAt, 'P p')} ~ {regularFormat(endAt, 'P p')}
        </dd>
        <dt>
          <T name="citizen_event_winners_title" />
        </dt>
        <dd>{regularFormat(announcedAt, 'P')}</dd>
      </DescriptionList>
      <Button
        className={classNames(heroStyles.button, heroStyles.fixedButton, {
          [styles.opened]: isLoading,
        })}
        size="large"
        disabled={buttonDisabled}
        onClick={handleButtonClick}
        data-event="click"
        data-event-category="top"
        data-event-target="adventure_top_join_btn">
        {!isInitialLoading && currentCount >= maxCount ? (
          <T name="citizen_join_button_completed" options={[currentCount]} />
        ) : (
          <T name="citizen_button_title_participatenow" />
        )}
      </Button>
    </BaseCard>
  );
};
